import { css, Interpolation, Theme } from '@emotion/react'
import { autocompleteClasses } from '@mui/material/Autocomplete'

export const wrapper: (fullWidth?: boolean) => Interpolation<Theme> = (fullWidth) =>
  css`
    width: ${fullWidth ? '100%' : '263px'};
  `

export const labelWrapper: Interpolation<Theme> = (theme) => css`
  width: 100%;
  margin: 0;
  align-items: initial;
  gap: 10px;

  & > .MuiTypography-root {
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: ${theme.typographyColors.typographyThree};
  }
`

type Size = 'small' | 'medium' | 'large'

const BUTTON_SIZES: Record<Size, number> = {
  small: 38,
  medium: 42,
  large: 48,
}

export const baseButton: (state: { open?: boolean; error?: boolean; size?: Size }) => Interpolation<Theme> =
  ({ open, error, size }) =>
  (theme) =>
    css`
      width: 100%;
      height: ${BUTTON_SIZES[size ?? 'large']}px;
      background: ${theme.backgrounds.backgroundFour};
      border: 1px solid ${error ? theme.colors.red : open ? theme.colors.blue : theme.backgrounds.backgroundOne};
      box-shadow: ${theme.boxShadows.small};
      border-radius: 14px;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      display: flex;
      color: ${theme.typographyColors.typographyTwo};

      &:focus {
        border: 1px solid ${error ? theme.colors.red : theme.colors.blue};
      }

      &:disabled {
        color: ${error ? theme.colors.red : theme.typographyColors.typographyThree};
      }

      span {
        width: 100%;
        text-align: left;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        padding-left: 14px;
        padding-right: 8px;
      }

      .placeholder {
        color: ${theme.typographyColors.typographyThree};
      }

      .chevron {
        min-width: 22px;
        margin-right: 14px;
        transform: rotate(${open ? 180 : 0}deg);
        transition: all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut};
      }
    `

export const popper: (width?: number) => Interpolation<Theme> = (width) => (theme) =>
  css`
    box-sizing: border-box;
    width: ${width ?? 263}px;
    padding: 8px 8px 0;
    background: ${theme.backgrounds.backgroundFour};
    border: 1px solid ${theme.backgrounds.backgroundOne};
    box-shadow: ${theme.boxShadows.medium};
    border-radius: 16px;
    overflow: hidden;
    z-index: 1400;
  `

export const autocompletePopper: Interpolation<Theme> = (theme) => css`
  width: 100% !important;

  & .${autocompleteClasses.paper} {
    box-shadow: none;
    margin: 0;
    color: inherit;
  }

  & .${autocompleteClasses.listbox} {
    max-height: 387px;
    background: ${theme.backgrounds.backgroundFour};
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 12px 0 8px;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: ${theme.typographyColors.typographyTwo};

    ::-webkit-scrollbar {
      display: none;
    }

    & .${autocompleteClasses.option} {
      position: relative;
      min-height: 44px;
      border-radius: 14px;
      padding: 10px 44px 10px 12px;

      &.${autocompleteClasses.focused}, &[aria-selected='true'], &.${autocompleteClasses.focused}[aria-selected="true"] {
        background: ${theme.colors.grayHover};
      }
    }
  }

  & .${autocompleteClasses.noOptions}, .${autocompleteClasses.loading} {
    height: 58px;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.typographyColors.typographyThree};
  }
`

export const checkIcon: Interpolation<Theme> = css`
  position: absolute;
  right: 12px;
`

export const searchInput: (isHidden?: boolean) => Interpolation<Theme> = (isHidden) => (theme) =>
  css`
    &.MuiInputBase-root {
      gap: 0;
      padding-right: 9px !important;
      color: ${theme.typographyColors.typographyTwo};

      ${isHidden &&
      css`
        height: 0;
        padding: 0;
        border: none;
        margin-bottom: -12px;
        overflow: hidden;
      `}
    }
  `

export const searchIcon: Interpolation<Theme> = css`
  min-width: 22px;
`

export const loader: Interpolation<Theme> = (theme) => css`
  min-width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;

  circle {
    stroke: ${theme.typographyColors.typographyTwo};
  }
`
